import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Undo2, X } from "lucide-react";
import { API, api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Doodles";

interface Pending { id: string; post_slug: string; post_title: string; first_name: string; note: string; status: string; created_at: string }
const TABS = [["pending", "Waiting"], ["approved", "Approved"], ["rejected", "Rejected"]] as const;

export default function PhotosPage() {
  const [tab, setTab] = useState<string>("pending");
  const [rows, setRows] = useState<Pending[] | null>(null);
  const load = useCallback(() => { api.get("/admin/user-photos", { params: { status: tab } }).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e))); }, [tab]);
  useEffect(() => { load(); }, [load]);
  const set = async (id: string, status: string) => {
    try { await api.patch(`/admin/user-photos/${id}`, { status }); toast.success(status === "approved" ? "Approved and live" : status === "rejected" ? "Rejected" : "Moved back to waiting"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="photos-page">
      <PageHeader title="Reader photos" kicker="Approve photos before they show on the site" />
      <div className="mb-5 flex gap-2">{TABS.map(([k, l]) => <button key={k} className={`chip ${tab === k ? "chip-active" : ""}`} onClick={() => setTab(k)} data-testid={`photos-tab-${k}`}>{l}</button>)}</div>
      {rows && rows.length === 0 && <p className="text-sm text-[#23324A]/60" data-testid="photos-empty">Nothing here.</p>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(rows || []).map((p) => (
          <div key={p.id} className="overflow-hidden rounded-2xl border-2 border-[#23324A]/20 bg-white" data-testid={`photo-row-${p.id}`}>
            <img src={`${API}/admin/user-photos/${p.id}/image`} alt={p.post_title} className="aspect-square w-full object-cover" loading="lazy" />
            <div className="space-y-2 p-3 text-sm text-[#23324A]">
              <p><b>{p.first_name}</b>{p.note ? `: ${p.note}` : ""}</p>
              <a href={`/${p.post_slug}`} target="_blank" rel="noreferrer" className="block truncate text-xs font-semibold text-[#D9785C] hover:underline">{p.post_title}</a>
              <p className="text-xs text-[#23324A]/60">Check: no children's faces, it's a real finished craft.</p>
              <div className="flex gap-2">
                {p.status !== "approved" && <button className="chip" onClick={() => set(p.id, "approved")} data-testid={`photo-approve-${p.id}`}><Check className="h-3 w-3" />Approve</button>}
                {p.status !== "rejected" && <button className="chip" onClick={() => set(p.id, "rejected")} data-testid={`photo-reject-${p.id}`}><X className="h-3 w-3" />Reject</button>}
                {p.status !== "pending" && <button className="chip" onClick={() => set(p.id, "pending")} data-testid={`photo-undo-${p.id}`}><Undo2 className="h-3 w-3" />Undo</button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
